import React, { useEffect, useState } from "react";

import type { IconName } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import ZoomRegionText from "@demo/components/Annotation/ZoomRegionText";
import {
  ImageSearchResultsList,
  SearchResultsList,
  TextImageResultsList,
  ToolCallCard,
  VisitResultsList,
} from "@demo/components/ToolCall";
import { describeVisitProgressHost } from "@demo/components/ToolCall/VisitResultsList";
import type { ToolCallFamily } from "@demo/libs/tool-calls/families";
import type { ToolCallBox } from "@demo/server/api/contracts/toolCall";
import { useMediaCarouselStore } from "@demo/store/media-carousel";
import { AgentState, useModelStore } from "@demo/store/model";
import { useToolCallStore, type ToolCallEntry } from "@demo/store/tool-calls";
import { getImageSrc } from "@demo/utils/media";

import { ToolCallRow, describeToolCallError, describeToolCallStatus } from "./ToolCallRow";

/** The pill's icon per family: the search glyph for everything that searches. The zoom renders no card. */
const FAMILY_ICON: Record<ToolCallFamily, IconName> = {
  search: "web-search",
  visit: "web-search",
  zoom: "scan-eye",
  generic: "web-search",
};

/** The summary shown beside the pill while collapsed: the quoted query for text-driven calls. */
const collapsedSummary = (call: ToolCallEntry): string | undefined => {
  const input = call.input;
  if (!input) return undefined;
  // A visit shows the page it is on (pages report in order), not the goal.
  if (input.kind === "visit") return describeVisitProgressHost(input.urls, call.links.length);
  if (input.kind === "image") return undefined;
  return `“${input.query}”`;
};

/**
 * The image a call with an `image` input was given, resolved from the run's media in asset_idx order. A
 * returned asset (from an earlier call) is not in it, so an index past the end simply has no preview.
 */
const useInputImage = (call: ToolCallEntry): { src?: string; region?: ToolCallBox } => {
  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const image = call.input?.kind === "image" ? call.input.image : undefined;
  const asset = image ? media[image.asset_idx] : undefined;
  return { src: asset ? getImageSrc(asset) || undefined : undefined, region: image?.region };
};

/**
 * The expanded body for a search-family call: reverse image search previews the searched image
 * and shows match tiles, text-to-image search shows the query and result tiles, web search shows
 * host rows.
 */
const SearchBody: React.FC<{ call: ToolCallEntry; isStreaming: boolean }> = ({ call, isStreaming }) => {
  const inputImage = useInputImage(call);
  const query = call.input?.kind === "text" || call.input?.kind === undefined ? call.input?.query : undefined;
  if (call.toolName === "search.image_search") {
    return (
      <ImageSearchResultsList
        inputImageSrc={inputImage.src}
        isStreaming={isStreaming}
        region={inputImage.region}
        results={call.links}
      />
    );
  }
  if (call.toolName === "search.text_image_search") {
    return <TextImageResultsList isStreaming={isStreaming} query={query} results={call.links} />;
  }
  return <SearchResultsList isStreaming={isStreaming} query={query} results={call.links} />;
};

/**
 * One tool call in the thought stream. The zoom is a chip, not a card: a region reference with a play
 * button that selects it on the image (`ZoomRegionText`). Every other call is a card with the body its
 * family is designed for: host rows for web search, a tile strip for text-to-image search, the searched
 * image and its matches for image search, pages with their summaries for visit; a tool of any other
 * family keeps the plain row inside the same shell.
 */
export const ToolCallEntryCard: React.FC<{ call: ToolCallEntry }> = ({ call }) =>
  call.family === "zoom" ? <ZoomRegionText call={call} /> : <ToolCallFamilyCard call={call} />;

const ToolCallFamilyCard: React.FC<{ call: ToolCallEntry }> = ({ call }) => {
  const expanded = useToolCallStore((state) => !!state.expanded[call.id]);
  const toggleExpanded = useToolCallStore((state) => state.toggleExpanded);
  const isRunActive = useModelStore(
    (state) => state.agentState === AgentState.STREAMING || state.agentState === AgentState.EVALUATING,
  );
  // Whether text has streamed since the result, selected as a flag: the text grows every frame, and a card
  // subscribed to its length would re-render every frame long after it settled.
  const hasTextSinceResult = useModelStore(
    (state) =>
      call.settledOutputLength !== null &&
      state.reasoningText.length + state.agentText.length > call.settledOutputLength,
  );
  const hasLaterCall = useToolCallStore((state) => {
    const position = state.order.indexOf(call.id);
    return position !== -1 && position < state.order.length - 1;
  });
  // After the result, the model takes a moment before it goes on. The card keeps its running look
  // (label, shimmer, placeholders) through that gap, so it settles as the model resumes rather than
  // leaving a still card above a pause: when more text streams, or, with no thinking to stream, when
  // the next call starts. Stopping the run settles it too.
  const isSettling =
    call.status === "completed" &&
    isRunActive &&
    !hasLaterCall &&
    call.settledOutputLength !== null &&
    !hasTextSinceResult;
  const isStreaming = call.status === "running" || isSettling;
  const isFailed = call.status === "failed";
  // Fades in the first time a call appears in a run; a card that remounts keeps still.
  const [fadeIn] = useState(() => !useToolCallStore.getState().entered.has(call.id));
  useEffect(() => {
    useToolCallStore.getState().entered.add(call.id);
  }, [call.id]);

  return (
    <ToolCallCard
      className={cn({ "tool-call-enter": fadeIn })}
      expanded={expanded}
      icon={FAMILY_ICON[call.family]}
      isStreaming={isStreaming}
      keepSummaryWhenExpanded={call.family === "visit"}
      label={describeToolCallStatus(call, isStreaming)}
      settledSummaryTone={call.family === "visit" ? "secondary" : "primary"}
      summary={isFailed ? describeToolCallError(call) : collapsedSummary(call)}
      tone={isFailed ? "error" : "default"}
      onToggle={() => toggleExpanded(call.id)}
    >
      {call.family === "search" && <SearchBody call={call} isStreaming={isStreaming} />}
      {call.family === "visit" && (
        <VisitResultsList
          goal={call.input?.kind === "visit" ? call.input.goal : undefined}
          isStreaming={isStreaming}
          results={call.links}
          urls={
            call.input?.kind === "visit"
              ? call.input.urls
              : call.links.flatMap(({ link }) => (link.url ? [link.url] : []))
          }
        />
      )}
      {call.family === "generic" && <ToolCallRow call={call} />}
    </ToolCallCard>
  );
};
